// TEST-EXEMPT: Visual/animation-only component with no testable logic
import { Suspense, useMemo, useRef, Component, type ReactNode } from 'react'
import { Canvas, useFrame } from '@react-three/fiber'
import { useGLTF, Float } from '@react-three/drei'
import * as THREE from 'three'
import type { GlbBackgroundConfig } from '@/hooks/use-glb-background'

/* ============================================================
   GLBBackgroundScene — Consumer-facing 3D background layer
   Renders multiple instances of a GLB model floating behind
   the tenant site content with configurable appearance/motion.
   ============================================================ */

/* ── Error boundary for the 3D canvas ────────────────── */
class CanvasErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean; error: Error | null }
> {
  state = { hasError: false, error: null as Error | null }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error) {
    console.warn('[GLBBackgroundScene] Canvas error caught:', error.message)
  }

  render() {
    if (this.state.hasError) return null
    return this.children
  }

  get children() {
    return this.props.children
  }
}

/* ── Single model instance with animation ────────────── */
function ModelInstance({
  url,
  color,
  opacity,
  scale,
  rotationSpeed,
  pattern,
  position,
  seed,
}: {
  url: string
  color: string
  opacity: number
  scale: number
  rotationSpeed: number
  pattern: GlbBackgroundConfig['movement_pattern']
  position: [number, number, number]
  seed: number
}) {
  const { scene: rawScene } = useGLTF(url)
  const groupRef = useRef<THREE.Group>(null)

  /* Clone scene once and apply color/opacity overrides */
  const scene = useMemo(() => {
    const cloned = rawScene.clone(true)

    /* Auto-fit: compute bounding box and normalize scale */
    const box = new THREE.Box3().setFromObject(cloned)
    const size = new THREE.Vector3()
    box.getSize(size)
    const maxDim = Math.max(size.x, size.y, size.z) || 1
    const normalizeScale = 2 / maxDim // fit into a ~2 unit cube
    cloned.scale.setScalar(normalizeScale)

    /* Center the model */
    const center = new THREE.Vector3()
    box.getCenter(center)
    cloned.position.sub(center.multiplyScalar(normalizeScale))

    cloned.traverse((child) => {
      if (child instanceof THREE.Mesh && child.material) {
        // Handle both single material and material arrays
        const materials = Array.isArray(child.material) ? child.material : [child.material]
        const newMaterials = materials.map((mat: THREE.Material) => {
          const clonedMat = mat.clone()
          // Apply color/opacity to any material with a color property
          if ('color' in clonedMat && clonedMat.color instanceof THREE.Color) {
            clonedMat.color.set(color)
          }
          if ('transparent' in clonedMat) {
            ;(clonedMat as THREE.MeshStandardMaterial).transparent = true
            ;(clonedMat as THREE.MeshStandardMaterial).opacity = opacity
            ;(clonedMat as THREE.MeshStandardMaterial).depthWrite = false
          }
          return clonedMat
        })
        child.material = Array.isArray(child.material) ? newMaterials : newMaterials[0]
      }
    })
    return cloned
  }, [rawScene, color, opacity])

  /* Animate based on movement pattern */
  useFrame((state) => {
    if (!groupRef.current) return
    const t = state.clock.elapsedTime + seed * 100

    const rs = rotationSpeed * 0.3
    groupRef.current.rotation.y = t * rs

    switch (pattern) {
      case 'orbit': {
        const radius = 2 + seed * 3
        groupRef.current.position.x = position[0] + Math.cos(t * 0.3 + seed * Math.PI * 2) * radius
        groupRef.current.position.z = position[2] + Math.sin(t * 0.3 + seed * Math.PI * 2) * radius
        groupRef.current.position.y = position[1] + Math.sin(t * 0.5 + seed * 5) * 0.5
        break
      }
      case 'drift': {
        groupRef.current.position.x = position[0] + Math.sin(t * 0.15 + seed * 10) * 3
        groupRef.current.position.y = position[1] + Math.sin(t * 0.2 + seed * 7) * 1.5
        groupRef.current.position.z = position[2]
        break
      }
      case 'scatter': {
        groupRef.current.position.x = position[0] + Math.sin(t * 0.1 + seed * 20) * 0.5
        groupRef.current.position.y = position[1] + Math.cos(t * 0.08 + seed * 15) * 0.3
        groupRef.current.position.z = position[2] + Math.sin(t * 0.12 + seed * 25) * 0.4
        groupRef.current.rotation.x = Math.sin(t * 0.2 + seed * 8) * 0.3
        break
      }
      case 'float':
      default:
        // Float handled by <Float> wrapper — just add subtle rotation
        groupRef.current.rotation.x = Math.sin(t * 0.3 + seed * 5) * 0.15
        break
    }
  })

  const content = (
    <group ref={groupRef} position={position}>
      <primitive object={scene} scale={scale} />
    </group>
  )

  /* Float pattern uses the drei Float wrapper; others animate directly */
  if (pattern === 'float') {
    return (
      <Float speed={1.5 + seed} rotationIntensity={0.3} floatIntensity={0.6 + seed * 0.3}>
        {content}
      </Float>
    )
  }

  return content
}

/* ── Generate deterministic random positions ──────────── */
function generatePositions(count: number): [number, number, number][] {
  const positions: [number, number, number][] = []
  // Use a seeded-ish distribution so positions are stable across re-renders
  for (let i = 0; i < count; i++) {
    const angle = (i / count) * Math.PI * 2
    const radius = 3 + (i % 3) * 2.5
    const x = Math.cos(angle) * radius + (((i * 7.3) % 3) - 1.5)
    const y = ((i * 3.7) % 6) - 3
    const z = Math.sin(angle) * radius * 0.6 + (((i * 5.1) % 2) - 1) - 3
    positions.push([x, y, z])
  }
  return positions
}

/* ── Main scene component ────────────────────────────── */
function Scene({ config }: { config: GlbBackgroundConfig }) {
  const positions = useMemo(() => generatePositions(config.instance_count), [config.instance_count])

  return (
    <>
      <ambientLight intensity={0.5} />
      <directionalLight position={[5, 8, 5]} intensity={0.7} />
      <pointLight position={[-4, -2, 3]} intensity={0.4} color={config.color_override} />
      <hemisphereLight args={['#ffffff', '#444444', 0.4]} />

      {positions.map((pos, i) => (
        <ModelInstance
          key={i}
          url={config.glb_url}
          color={config.color_override}
          opacity={config.opacity}
          scale={config.scale * (0.6 + (i % 3) * 0.3)}
          rotationSpeed={config.rotation_speed}
          pattern={config.movement_pattern}
          position={pos}
          seed={i / config.instance_count}
        />
      ))}
    </>
  )
}

/* ── Public component ────────────────────────────────── */
export default function GLBBackgroundScene({ config }: { config: GlbBackgroundConfig }) {
  if (!config.enabled || !config.glb_url) return null

  return (
    <CanvasErrorBoundary>
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 0,
          pointerEvents: 'none',
          opacity: 1,
        }}
        aria-hidden="true"
      >
        <Canvas
          camera={{ position: [0, 0, 12], fov: 60 }}
          gl={{
            antialias: true,
            alpha: true,
            powerPreference: 'high-performance',
            premultipliedAlpha: false,
          }}
          style={{ background: 'transparent', width: '100%', height: '100%' }}
          dpr={[1, 1.5]}
          frameloop="always"
          onCreated={({ gl, scene }) => {
            gl.setClearColor(0x000000, 0)
            scene.background = null
            gl.outputColorSpace = THREE.SRGBColorSpace
            gl.toneMapping = THREE.ACESFilmicToneMapping
            gl.toneMappingExposure = 1.2
          }}
        >
          <Suspense fallback={null}>
            <Scene config={config} />
          </Suspense>
        </Canvas>
      </div>
    </CanvasErrorBoundary>
  )
}
